import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen,
  Building2,
  ShieldCheck,
  Users,
  UserCircle,
  Package,
  FileText,
  Calendar,
  Download,
  CheckCircle2,
  Lock,
  Trash2,
  UserCog,
  PlayCircle,
  Mail,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = '#5A7D6C';

const Step = ({ n, children }) => (
  <li className="flex gap-3">
    <span
      className="flex-shrink-0 h-6 w-6 rounded-full text-white text-xs font-bold flex items-center justify-center mt-0.5"
      style={{ backgroundColor: GREEN }}
    >
      {n}
    </span>
    <span className="text-[#2D3748] leading-relaxed">{children}</span>
  </li>
);

const Section = ({ icon: Icon, title, badge, children, id }) => (
  <Card className="p-6 mb-6" id={id} data-testid={`manual-section-${id}`}>
    <div className="flex items-center gap-3 mb-4">
      <div
        className="h-11 w-11 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: '#EAF0EC' }}
      >
        <Icon className="h-6 w-6" style={{ color: GREEN }} />
      </div>
      <div>
        <h2 className="text-lg md:text-xl font-bold text-[#2D3748]">{title}</h2>
        {badge && <span className="text-xs text-[#718096]">{badge}</span>}
      </div>
    </div>
    <div className="space-y-3 text-sm md:text-base">{children}</div>
  </Card>
);

const ManualPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isWelcome = searchParams.get('welcome') === '1';

  const handleDownload = () => {
    window.open(`${API}/manual/download`, '_blank');
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="manual-page">
      {isWelcome && (
        <Card
          className="p-5 mb-6 border-none"
          style={{ backgroundColor: GREEN }}
          data-testid="manual-welcome-banner"
        >
          <div className="flex items-start gap-3">
            <BookOpen className="h-7 w-7 text-white flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg md:text-xl font-bold text-white">
                ¡Bienvenido/a a REKORD{user?.name ? `, ${user.name}` : ''}!
              </h2>
              <p className="text-white/90 text-sm mt-1">
                Esta es tu guía de inicio. Dedica un minuto a leerla para empezar a gestionar tu centro.
                Podrás volver aquí cuando quieras desde el menú <strong>“Ayuda / Manual”</strong>.
              </p>
            </div>
          </div>
        </Card>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <BookOpen className="h-8 w-8" style={{ color: GREEN }} />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2D3748]">
              Manual de Uso — REKORD
            </h1>
            <p className="text-sm text-[#718096]">
              Guía del Super Administrador para gestionar instituciones, usuarios e informes
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => window.open(`${API}/manual/invitations/download`, '_blank')}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="manual-invite-guide-btn"
          >
            <Download className="h-4 w-4" />
            Guía de Invitación (PDF)
          </Button>
          <Button
            onClick={handleDownload}
            className="flex items-center gap-2"
            style={{ backgroundColor: GREEN }}
            data-testid="manual-download-btn"
          >
            <Download className="h-4 w-4" />
            Descargar PDF
          </Button>
        </div>
      </div>

      {/* Intro */}
      <Card className="p-6 mb-6" style={{ backgroundColor: '#EAF0EC', border: 'none' }}>
        <p className="text-sm text-[#2D3748] leading-relaxed">
          REKORD es una plataforma <strong>multi-tenant</strong>: cada clínica o centro es una
          <strong> Institución</strong> independiente y sus datos (pacientes, terapeutas, informes,
          citas y recursos) están totalmente <strong>aislados</strong> del resto. Este manual
          describe, paso a paso, cómo operar el sistema desde el rol de <strong>Super Administrador</strong>.
        </p>
      </Card>

      {/* 1. Alta de institución */}
      <Section
        id="alta-institucion"
        icon={Building2}
        title="1. Dar de alta una nueva Institución"
        badge="Solo Super Administrador"
      >
        <p className="text-[#4A5568]">
          Al crear una institución, el sistema le asigna un identificador único e independiente. Ningún
          dato podrá mezclarse con otras instituciones existentes o futuras.
        </p>
        <ol className="space-y-3 mt-2">
          <Step n="1">
            Inicia sesión con tu cuenta de <strong>Super Administrador</strong>.
          </Step>
          <Step n="2">
            En el menú lateral izquierdo, entra en <strong>“Panel Global”</strong>.
          </Step>
          <Step n="3">
            Pulsa el botón <strong>“Crear Nueva Institución”</strong> (arriba, en la tarjeta de Filtros).
          </Step>
          <Step n="4">
            Completa los datos de la institución: <strong>Nombre</strong> (obligatorio), Dirección y Teléfono.
          </Step>
          <Step n="5">
            Registra su primer <strong>Administrador</strong>: nombre, email y una contraseña inicial.
            Este administrador gestionará esa institución de forma autónoma.
          </Step>
          <Step n="6">
            Pulsa <strong>“Crear Institución”</strong>. Verás un mensaje de confirmación y la nueva
            institución aparecerá en las estadísticas y en el filtro de instituciones.
          </Step>
          <Step n="7">
            Entrega al administrador su email y contraseña. Al iniciar sesión verá su institución
            <strong> completamente vacía</strong>, lista para empezar de cero.
          </Step>
        </ol>
        <div className="flex items-start gap-2 mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-800">
            <strong>Garantía de aislamiento:</strong> cada institución nace con su propio identificador.
            Los pacientes, informes y citas creados por un centro jamás serán visibles para otro.
          </p>
        </div>
      </Section>

      {/* 2. Multi-tenant */}
      <Section
        id="multi-tenant"
        icon={ShieldCheck}
        title="2. Cómo garantizo que el sistema es Multi-Tenant"
        badge="Aislamiento y seguridad de datos"
      >
        <p className="text-[#4A5568]">
          REKORD separa la información por institución de forma automática en cada consulta a la base
          de datos. Así se garantiza que los datos de distintas instituciones nunca se mezclan:
        </p>
        <ul className="space-y-2 mt-2">
          <li className="flex gap-2">
            <Lock className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: GREEN }} />
            <span>
              <strong>Filtro automático por institución:</strong> cada usuario (Administrador, Terapeuta,
              Solo Lectura) solo ve los registros de <em>su</em> institución. El sistema añade el filtro
              de forma transparente en todas las secciones.
            </span>
          </li>
          <li className="flex gap-2">
            <Lock className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: GREEN }} />
            <span>
              <strong>Validación al editar o borrar:</strong> antes de modificar un paciente, informe o
              cita, el sistema verifica que el registro pertenece a la institución del usuario.
            </span>
          </li>
          <li className="flex gap-2">
            <Lock className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: GREEN }} />
            <span>
              <strong>Solo el Super Admin ve todo:</strong> únicamente el Super Administrador puede
              consultar la vista global consolidada de todas las instituciones.
            </span>
          </li>
        </ul>

        <p className="text-[#2D3748] font-semibold mt-4">Cómo comprobarlo tú mismo (prueba rápida):</p>
        <ol className="space-y-3 mt-2">
          <Step n="1">Crea dos instituciones distintas (por ejemplo, “Centro A” y “Centro B”).</Step>
          <Step n="2">
            Inicia sesión como Administrador del Centro A y crea un paciente de prueba.
          </Step>
          <Step n="3">
            Cierra sesión e inicia como Administrador del Centro B: su listado de pacientes debe estar
            <strong> vacío</strong> (no verá el paciente del Centro A).
          </Step>
          <Step n="4">
            Repite la comprobación con Informes y Citas. Cada centro solo ve lo suyo.
          </Step>
        </ol>
      </Section>

      {/* 3. Secciones */}
      <Section
        id="secciones"
        icon={Users}
        title="3. Cómo usar las diferentes secciones"
        badge="Terapeutas, Pacientes, Recursos, Informes y Calendario"
      >
        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <UserCircle className="h-5 w-5" style={{ color: GREEN }} />
              <h3 className="font-semibold text-[#2D3748]">Terapeutas</h3>
            </div>
            <p className="text-[#4A5568]">
              En <strong>“Terapeutas”</strong> registra a los profesionales del centro (nombre, email,
              teléfono, especialización y foto). Se asignan a las sesiones e informes. Puedes editar o
              eliminar con los iconos de cada fila.
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-5 w-5" style={{ color: GREEN }} />
              <h3 className="font-semibold text-[#2D3748]">Pacientes</h3>
            </div>
            <p className="text-[#4A5568]">
              En <strong>“Pacientes”</strong> das de alta a cada paciente con sus datos personales,
              diagnóstico, historia clínica, alergias, medicación, contacto de emergencia y anamnesis.
              Puedes adjuntar archivos médicos (PDF) y una fotografía.
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <Package className="h-5 w-5" style={{ color: GREEN }} />
              <h3 className="font-semibold text-[#2D3748]">Recursos</h3>
            </div>
            <p className="text-[#4A5568]">
              En <strong>“Recursos”</strong> registra el material de la sala multisensorial (proyectores,
              columnas de burbujas, aromas, etc.). Estos recursos se pueden asociar a cada sesión en el informe.
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-5 w-5" style={{ color: GREEN }} />
              <h3 className="font-semibold text-[#2D3748]">Calendario / Citas</h3>
            </div>
            <p className="text-[#4A5568]">
              En <strong>“Calendario”</strong> agenda sesiones seleccionando paciente, terapeuta, fecha,
              hora y duración. Puedes enviar recordatorios y luego generar el informe de esa sesión.
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <FileText className="h-5 w-5" style={{ color: GREEN }} />
              <h3 className="font-semibold text-[#2D3748]">Informes de Sesión</h3>
            </div>
            <p className="text-[#4A5568]">
              En <strong>“Informes”</strong> creas el registro detallado de cada sesión multisensorial
              (ver punto 4). Cada informe puede editarse, eliminarse y <strong>exportarse a PDF</strong>.
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="h-5 w-5" style={{ color: GREEN }} />
              <h3 className="font-semibold text-[#2D3748]">Otras secciones</h3>
            </div>
            <p className="text-[#4A5568]">
              <strong>Institución</strong> (datos y logo), <strong>Usuarios / Admin Panel</strong>
              (invitar y gestionar profesionales), <strong>Analytics</strong> (evolución del paciente),
              <strong> Auditoría</strong> (registro de acciones) y <strong>Panel Global</strong> (solo Super Admin).
            </p>
          </div>
        </div>
      </Section>

      {/* 4. Contenido del informe */}
      <Section
        id="informe"
        icon={FileText}
        title="4. Qué características puedo anotar en un informe"
        badge="Estructura del informe de sesión multisensorial"
      >
        <p className="text-[#4A5568]">
          Cada informe de sesión permite registrar la siguiente información. Al terminar, puedes
          exportarlo a PDF con el logo y datos de la institución.
        </p>

        <div className="mt-3">
          <h3 className="font-semibold text-[#2D3748] mb-1">Datos generales</h3>
          <div className="flex flex-wrap gap-2">
            {['Paciente', 'Terapeuta', 'Fecha de sesión', 'Asistencia / Ausencia', 'Motivo de ausencia', 'Objetivo', 'Duración (min)', 'Recursos utilizados'].map((f) => (
              <Badge key={f} variant="outline">{f}</Badge>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <h3 className="font-semibold text-[#2D3748] mb-1">Métricas de aceptación</h3>
          <p className="text-[#4A5568] text-sm mb-2">
            Cada ítem se valora en niveles: <em>NO · 25-50% · 50-75% · 75-100%</em>.
          </p>
          <div className="flex flex-wrap gap-2">
            {['Acepta el espacio', 'Acepta la propuesta', 'Rechaza la propuesta', 'Interactúa con el profesional', 'Sostiene la actividad', 'Registro de salida'].map((f) => (
              <Badge key={f} variant="outline">{f}</Badge>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <h3 className="font-semibold text-[#2D3748] mb-1">Estados emocionales</h3>
          <p className="text-[#4A5568] text-sm">
            Se marcan los estados observados y en qué momento aparecen (al inicio, durante y al final de la sesión).
          </p>
        </div>

        <div className="mt-4">
          <h3 className="font-semibold text-[#2D3748] mb-1">Habilidades evaluadas</h3>
          <div className="flex flex-wrap gap-2">
            {['Habilidades motoras', 'Habilidades de procesamiento', 'Habilidades sociales'].map((f) => (
              <Badge key={f} variant="outline">{f}</Badge>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <h3 className="font-semibold text-[#2D3748] mb-1">Registro cualitativo</h3>
          <div className="flex flex-wrap gap-2">
            {['Estímulos aplicados', 'Observaciones', 'Campos personalizados'].map((f) => (
              <Badge key={f} variant="outline">{f}</Badge>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-2 mt-5 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-800">
            <strong>Plantillas personalizables:</strong> cada institución puede crear su propia plantilla
            de informe con secciones y campos a medida (texto, selección, casilla, número, fecha), o usar
            la plantilla SNOEZELEN estándar incluida.
          </p>
        </div>
      </Section>

      {/* 5. Gestionar instituciones existentes */}
      <Section
        id="gestion-instituciones"
        icon={Trash2}
        title="5. Gestionar instituciones existentes"
        badge="Activar · Desactivar · Eliminar (Super Administrador)"
      >
        <p className="text-[#4A5568]">
          Desde el <strong>Panel Global</strong>, en la tabla <strong>“Instituciones”</strong>, puedes
          controlar cada centro sin afectar a los demás.
        </p>
        <div className="mt-2">
          <h3 className="font-semibold text-[#2D3748] mb-1">Desactivar / Activar (reversible)</h3>
          <ul className="space-y-2">
            <li className="flex gap-2">
              <Lock className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: GREEN }} />
              <span>
                Al pulsar <strong>“Desactivar”</strong>, los usuarios de esa institución <strong>no
                podrán iniciar sesión</strong>, pero todos sus datos se conservan intactos.
              </span>
            </li>
            <li className="flex gap-2">
              <Lock className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: GREEN }} />
              <span>
                Pulsa <strong>“Activar”</strong> para restaurar el acceso en cualquier momento.
              </span>
            </li>
          </ul>
        </div>
        <div className="mt-4">
          <h3 className="font-semibold text-[#2D3748] mb-1">Eliminar (con papelera de 30 días)</h3>
          <ol className="space-y-3 mt-1">
            <Step n="1">Pulsa el icono de papelera de la institución.</Step>
            <Step n="2">
              Por seguridad, deberás <strong>escribir el nombre exacto</strong> de la institución para confirmar.
            </Step>
            <Step n="3">
              La institución pasa a la <strong>Papelera</strong>: sus usuarios no podrán iniciar sesión, pero
              los datos se conservan durante <strong>30 días</strong>.
            </Step>
            <Step n="4">
              Desde la sección <strong>“Papelera de Instituciones”</strong> puedes <strong>Restaurar</strong> el
              centro en cualquier momento, o <strong>eliminarlo definitivamente</strong> (irreversible).
            </Step>
          </ol>
          <div className="flex items-start gap-2 mt-4 p-3 rounded-lg bg-red-50 border border-red-200">
            <Trash2 className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">
              Pasados 30 días, las instituciones de la papelera se eliminan <strong>automáticamente</strong> y de
              forma permanente. No es posible eliminar tu propia institución ni la institución principal del sistema.
            </p>
          </div>
        </div>
      </Section>

      {/* 6. Guía por rol */}
      <Section
        id="roles"
        icon={UserCog}
        title="6. Guía rápida por rol"
        badge="Qué puede hacer cada tipo de usuario"
      >
        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <ShieldCheck className="h-5 w-5" style={{ color: GREEN }} />
              <h3 className="font-semibold text-[#2D3748]">Administrador</h3>
              <Badge className="bg-blue-100 text-blue-800">Gestión completa del centro</Badge>
            </div>
            <ul className="space-y-2 mt-1">
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-1 flex-shrink-0 text-emerald-600" /><span>Da de alta y edita <strong>pacientes, terapeutas y recursos</strong> de su institución.</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-1 flex-shrink-0 text-emerald-600" /><span>Crea, edita, elimina y exporta <strong>informes de sesión</strong>.</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-1 flex-shrink-0 text-emerald-600" /><span>Agenda <strong>citas</strong> y envía recordatorios.</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-1 flex-shrink-0 text-emerald-600" /><span>Invita y gestiona <strong>usuarios</strong> (terapeutas, solo lectura) desde “Usuarios” / “Admin Panel”.</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-1 flex-shrink-0 text-emerald-600" /><span>Edita los datos de la <strong>institución</strong> (nombre, logo) y consulta la <strong>auditoría</strong>.</span></li>
            </ul>
            <p className="text-sm text-[#718096] mt-2">Nota: el Administrador solo ve y gestiona los datos de <em>su</em> institución.</p>
          </div>

          <div className="pt-3 border-t">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <UserCircle className="h-5 w-5" style={{ color: GREEN }} />
              <h3 className="font-semibold text-[#2D3748]">Terapeuta</h3>
              <Badge className="bg-green-100 text-green-800">Trabajo clínico diario</Badge>
            </div>
            <ul className="space-y-2 mt-1">
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-1 flex-shrink-0 text-emerald-600" /><span>Da de alta y edita <strong>pacientes</strong>.</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-1 flex-shrink-0 text-emerald-600" /><span>Crea, edita, elimina y exporta <strong>informes de sesión</strong>.</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-1 flex-shrink-0 text-emerald-600" /><span><strong>Consulta</strong> terapeutas y recursos del centro (sin modificarlos).</span></li>
              <li className="flex gap-2"><Lock className="h-4 w-4 mt-1 flex-shrink-0 text-gray-500" /><span>No gestiona usuarios ni la configuración de la institución.</span></li>
            </ul>
          </div>

          <div className="pt-3 border-t">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <UserCircle className="h-5 w-5" style={{ color: GREEN }} />
              <h3 className="font-semibold text-[#2D3748]">Solo Lectura</h3>
              <Badge className="bg-gray-100 text-gray-800">Consulta</Badge>
            </div>
            <p className="text-[#4A5568] mt-1">
              Puede <strong>ver</strong> pacientes, terapeutas, informes y recursos de su institución, pero
              no puede crear, editar ni eliminar nada. Ideal para supervisión o auditoría externa.
            </p>
          </div>
        </div>
      </Section>

      {/* 7. Video tutorial */}
      <Section
        id="video"
        icon={PlayCircle}
        title="7. Video tutorial"
        badge="Onboarding para nuevos centros"
      >
        <div className="rounded-xl border border-dashed border-[#CBD5E0] bg-[#F7FAFC] p-10 flex flex-col items-center justify-center text-center">
          <PlayCircle className="h-14 w-14 mb-3" style={{ color: GREEN }} />
          <p className="font-semibold text-[#2D3748]">Video guiado — Próximamente</p>
          <p className="text-sm text-[#718096] mt-1 max-w-md">
            Aquí se incrustará un breve video de bienvenida para que los nuevos centros aprendan a usar
            REKORD en pocos minutos.
          </p>
        </div>
      </Section>

      {/* Apéndice Resend */}
      <Section
        id="resend"
        icon={Mail}
        title="Apéndice: activar el envío real de emails (Resend)"
        badge="Verificación de dominio"
      >
        <p className="text-[#4A5568]">
          Por defecto, los correos de invitación funcionan en modo de pruebas (solo llegan a direcciones
          verificadas). Para que lleguen a cualquier email, hay que verificar tu dominio en Resend:
        </p>
        <ol className="space-y-3 mt-2">
          <Step n="1">Entra en <strong>resend.com</strong> y accede a tu cuenta.</Step>
          <Step n="2">Ve a <strong>Domains → Add Domain</strong> e introduce tu dominio (ej: <em>tudominio.com</em>).</Step>
          <Step n="3">Copia los registros <strong>DNS (SPF, DKIM y DMARC)</strong> que te muestra Resend y añádelos en tu proveedor de dominio.</Step>
          <Step n="4">Espera a que Resend marque el dominio como <strong>“Verified”</strong> (suele tardar unos minutos).</Step>
          <Step n="5">Indícanos el correo remitente que quieres usar (ej: <em>notificaciones@tudominio.com</em>) para configurarlo en la app.</Step>
        </ol>
        <div className="flex items-start gap-2 mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
          <Mail className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800">
            Mientras el dominio no esté verificado, puedes seguir invitando profesionales y compartir el
            <strong> código de invitación</strong> manualmente (desmarcando “Enviar por email automáticamente”).
          </p>
        </div>
      </Section>


      <p className="text-center text-xs text-[#718096] mt-8 mb-4">
        REKORD · Manual de uso — {new Date().getFullYear()}
      </p>
    </div>
  );
};

export default ManualPage;
